import { Routes } from '@angular/router';
// Keep critical components as direct imports for faster initial load
import { LoginComponent } from './pages/login/login.component';
import { AuthGuard } from './guards/auth.guard';
import { OnboardingGuard, TierSelectionGuard } from './guards/onboarding.guard';
import { AppLayoutComponent } from './layouts/app-layout/app-layout.component';
import { CompleteProfileComponent } from './pages/complete-profile/complete-profile.component';
// Child components still need direct imports for nested routes
import { SequenceMetricsComponent } from './components/sequence-metrics/sequence-metrics.component';
import { LinkedInSequenceDetailsComponent } from './components/linkedin-outreach/linkedin-sequence-details/linkedin-sequence-details.component';
import { EmailSequenceDetailsComponent } from './components/email-outreach/email-sequence-details/email-sequence-details.component';
import { LinkedinMainComponent } from './pages/linkedin/linkedin-main/linkedin-main.component';
import { EmailOutreachMainComponent } from './pages/email-outreach/email-outreach-main/email-outreach-main.component';

// Routes for Angular 19 standalone components
export const routes: Routes = [
  {
    path: 'login',
    component: LoginComponent
  },
  {
    // Deep-link target for the marketing site: opens the Create Account tab.
    path: 'signup',
    component: LoginComponent,
    data: { signup: true }
  },
  {
    path: 'accept-invitation/:token',
    loadComponent: () => import('./pages/accept-invitation/accept-invitation.component').then(m => m.AcceptInvitationComponent)
  },
  { path: 'complete-profile', component: CompleteProfileComponent },
  {
    path: 'select-plan',
    canActivate: [TierSelectionGuard],
    loadComponent: () => import('./pages/tier-selection/tier-selection.component').then(m => m.TierSelectionComponent)
  },
  {
    path: 'setup',
    canActivate: [AuthGuard],
    loadComponent: () => import('./pages/setup-wizard/setup-wizard.component').then(m => m.SetupWizardComponent),
    data: { title: 'Setup Your Workspace' }
  },
  {
    path: '',
    component: AppLayoutComponent,
    canActivate: [AuthGuard, OnboardingGuard],
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      { 
        path: 'dashboard', 
        loadComponent: () => import('./pages/dashboard/dashboard.component').then(m => m.DashboardComponent)
      },
      { 
        path: 'email-outreach', 
        loadComponent: () => import('./pages/email-outreach/email-outreach.component').then(m => m.EmailOutreachComponent),
        children: [
          { path: '', component: EmailOutreachMainComponent },
          { path: 'sequence/:id/:companyId', component: EmailSequenceDetailsComponent }
        ]
      },
      { 
        path: 'linkedin', 
        loadComponent: () => import('./pages/linkedin/linkedin.component').then(m => m.LinkedInComponent),
        children: [
          {path:'', component:LinkedinMainComponent},
          { path: 'metrics/:id/:companyId', component: SequenceMetricsComponent },
          { path: 'details/:id/:companyId', component: LinkedInSequenceDetailsComponent }
        ]
       },
      
      { 
        path: 'inbox', 
        loadComponent: () => import('./pages/inbox/inbox.component').then(m => m.InboxComponent)
      },
      { 
        path: 'analytics', 
        loadComponent: () => import('./pages/analytics/analytics.component').then(m => m.AnalyticsComponent)
      },
      { 
        path: 'ab-testing', 
        loadComponent: () => import('./pages/ab-testing/ab-testing.component').then(m => m.ABTestingComponent)
      },
      { 
        path: 'calls', 
        loadComponent: () => import('./pages/email-outreach/email-outreach.component').then(m => m.EmailOutreachComponent)
      },
      { 
        path: 'messaging', 
        loadComponent: () => import('./pages/email-outreach/email-outreach.component').then(m => m.EmailOutreachComponent)
      },
      { 
        path: 'integrations', 
        loadComponent: () => import('./pages/integrations/integrations.component').then(m => m.IntegrationsComponent)
      },
      {
        path: 'patterns',
        loadComponent: () => import('./pages/patterns/patterns.component').then(m => m.PatternComponent)
      },
      {
        path: 'strategy',
        loadComponent: () => import('./pages/messaging-map/messaging-map.component').then(m => m.MessagingMapComponent),
        canDeactivate: [(component: { confirmLeave?: () => boolean }) => component.confirmLeave?.() ?? true]
      },
      {
        path: 'mcp-access',
        loadComponent: () => import('./pages/mcp-access/mcp-access.component').then(m => m.McpAccessComponent)
      },
      { 
        path: 'settings', 
        loadComponent: () => import('./pages/user-settings/user-settings.component').then(m => m.UserSettingsComponent)
      },
      {
        path: 'users',
        loadComponent: () => import('./pages/company-users/company-users.component').then(m => m.CompanyUsersComponent)        
      },
      {
        path: 'users/:companyId',
        loadComponent: () => import('./pages/company-users/company-users.component').then(m => m.CompanyUsersComponent)        
      },
      {
        path: 'company-management',
        loadComponent: () => import('./pages/company-management/company-management.component').then(m => m.CompanyManagementComponent)
      },
      {
        path: 'billing',
        loadComponent: () => import('./pages/billing/billing.component').then(m => m.BillingComponent)
      },
      {
        path: 'settings/notifications',
        loadComponent: () => import('./pages/email-preferences/email-preferences.component').then(m => m.EmailPreferencesComponent)
      },
      {
        path: 'support',
        loadComponent: () => import('./pages/support/support.component').then(m => m.SupportComponent)
      },

    ]
  },
  // Fallback route
  { path: '**', redirectTo: '/login' }
]
